import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxItem, ComboboxList, ComboboxTrigger, ComboboxValue } from "@/ds/shadcn/components/ui/combobox";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { cn } from "@/ds/utils";
import type { PaginatedInfiniteQueryResult } from "@/lib/common/PaginatedInfiniteQueryResult";
import { useInfiniteScroll } from "@/lib/common/useInfiniteScroll";
import { useMemo, useRef, type ComponentType, type RefObject } from "react";

export interface InfiniteComboBoxProps<T extends object> {
	value: T | null;
	onChange: (value: T | null) => void;
	forQuery: PaginatedInfiniteQueryResult<T>;
	optionComponent: ComponentType<T>;
	filter?: (value: T) => boolean;
	placeholder?: string;
	className?: string;
	disabled?: boolean;
}

export const InfiniteComboBox = <T extends object>(props: InfiniteComboBoxProps<T>): React.JSX.Element => {
	const {
		value,
		onChange,
		forQuery,
		optionComponent: OptionComponent,
		filter,
		placeholder = "Select an option...",
		className,
		disabled = false
	} = props;

	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const listRef = useRef<HTMLDivElement | null>(null);

	const items = useMemo(() => {
		const items = forQuery.data?.pages.flatMap(x => x.results) ?? [];
		return filter
			? items.filter(filter)
			: items;
	}, [forQuery.data]);

	return (
		<Combobox items={items} value={value} onValueChange={onChange} disabled={disabled} filter={null}>
			<ComboboxTrigger
				ref={triggerRef}
				disabled={disabled}
				className={cn(
					"border-input data-placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 dark:hover:bg-input/50 flex h-9 w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
					className
				)}>
				<ComboboxValue>
					{
						(item: T | null) => item
							? <OptionComponent {...item} />
							: placeholder
					}
				</ComboboxValue>
			</ComboboxTrigger>
			<ComboboxContent anchor={triggerRef}>
				<ComboboxEmpty>
					{forQuery.isLoading ? null : "No results found."}
				</ComboboxEmpty>
				<ComboboxList ref={listRef}>
					{
						(item: T, index: number) => (
							<ComboboxItem key={index} value={item}>
								<OptionComponent {...item} />
							</ComboboxItem>
						)
					}
				</ComboboxList>
				<InfiniteComboBoxScroller forQuery={forQuery} scrollRef={listRef} />
			</ComboboxContent>
		</Combobox>
	);
};

interface InfiniteComboBoxScrollerProps<T> {
	forQuery: PaginatedInfiniteQueryResult<T>;
	scrollRef: RefObject<HTMLDivElement | null>;
}

const InfiniteComboBoxScroller = <T,>(props: InfiniteComboBoxScrollerProps<T>): React.JSX.Element => {
	const {
		forQuery,
		scrollRef
	} = props;

	const ref = useRef<HTMLDivElement | null>(null);
	const showSpinner = useInfiniteScroll(ref, forQuery, scrollRef);

	return (
		<div ref={ref} className={cn("flex justify-center p-3", !showSpinner && "hidden")}>
			<Spinner />
		</div>
	);
};
